import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../dashboard/components/AdminLayout';
import { useAdminForms } from '../hooks/useAdminForms';
import { QuestionList } from '../components/QuestionList';
import { QuestionDrawer } from '../components/QuestionDrawer';
import { SubmissionList } from '../components/SubmissionList';
import { SchemaDrawer } from '../components/SchemaDrawer';
import { SubmissionDrawer } from '../components/SubmissionDrawer';
import { SchemaVersionsDrawer } from '../components/SchemaVersionsDrawer';
import { GenerateSchemaDrawer } from '../components/GenerateSchemaDrawer';
import { CreateSchemaDrawer } from '../components/CreateSchemaDrawer';
import { FormSchema, FormSubmission } from '../types';
import { api } from '../../../../core/utils/apiClient';
import { COLORS } from '../../../../core/constants/colors';

type TabType = 'questions' | 'schemas' | 'submissions';

export const AdminFormsPage: React.FC = () => {
  const { schemas, questions, submissions, isLoading, loadData } = useAdminForms();
  const [activeTab, setActiveTab] = useState<TabType>('questions');
  const [showCreateQuestionDrawer, setShowCreateQuestionDrawer] = useState(false);
  const [showCreateSchemaDrawer, setShowCreateSchemaDrawer] = useState(false);
  const [showGenerateSchemaDrawer, setShowGenerateSchemaDrawer] = useState(false);
  const [showSchemaDrawer, setShowSchemaDrawer] = useState(false);
  const [schemaDrawerMode, setSchemaDrawerMode] = useState<'view' | 'edit'>('view');
  const [showSchemaVersionsDrawer, setShowSchemaVersionsDrawer] = useState(false);
  const [selectedSchema, setSelectedSchema] = useState<FormSchema | null>(null);
  const [selectedSchemaId, setSelectedSchemaId] = useState<number | null>(null);
  const [showSubmissionDrawer, setShowSubmissionDrawer] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<FormSubmission | null>(null);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<number | null>(null);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCompanyId, setFilterCompanyId] = useState<number | null>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [schemaCurrentPage, setSchemaCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (activeTab === 'submissions') {
      loadCompanies();
    }
  }, [activeTab]);

  const loadCompanies = async () => {
    try {
      const response = await api.get('/api/v1/companies/');
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const handleQuestionSuccess = () => {
    loadData();
    setShowCreateQuestionDrawer(false);
  };

  const handleSchemaSuccess = () => {
    loadData();
    setShowCreateSchemaDrawer(false);
    setShowGenerateSchemaDrawer(false);
    setShowSchemaDrawer(false);
    setSelectedSchema(null);
  };

  const handleViewSchema = (schema: FormSchema) => {
    setSelectedSchema(schema);
    setSchemaDrawerMode('view');
    setShowSchemaDrawer(true);
  };

  const handleEditSchema = (schema: FormSchema) => {
    setSelectedSchema(schema);
    setSchemaDrawerMode('edit');
    setShowSchemaDrawer(true);
  };

  const handleViewVersions = (schemaId: number) => {
    setSelectedSchemaId(schemaId);
    setShowSchemaVersionsDrawer(true);
  };

  const handleViewSubmission = (submission: FormSubmission) => {
    setSelectedSubmission(submission);
    setShowSubmissionDrawer(true);
  };

  // Filter schemas
  const filteredSchemas = schemas.filter((schema) => {
    const matchesSearch = schema.form_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (schema.form_description || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || schema.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const paginatedSchemas = filteredSchemas.slice(
    (schemaCurrentPage - 1) * itemsPerPage,
    schemaCurrentPage * itemsPerPage
  );

  const schemaTotalPages = Math.ceil(filteredSchemas.length / itemsPerPage);

  const tabs = [
    { id: 'questions' as TabType, name: 'Question Bank', count: questions.length },
    { id: 'schemas' as TabType, name: 'Form Schemas', count: schemas.length },
    { id: 'submissions' as TabType, name: 'Submissions', count: submissions.length },
  ];

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, { bg: string; text: string }> = {
      active: { bg: '#D1FAE5', text: '#059669' },
      draft: { bg: '#FEF3C7', text: '#D97706' },
      archived: { bg: '#F3F4F6', text: '#6B7280' },
    };
    return statusColors[status] || { bg: '#F3F4F6', text: '#6B7280' };
  };

  if (isLoading) {
  return (
      <AdminLayout pageTitle="Forms Management" pageDescription="Manage questions, form schemas, and submissions">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
              style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
            />
            <p style={{ color: '#6B7280' }}>Loading forms...</p>
            </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="Forms Management" pageDescription="Manage questions, form schemas, and submissions">
      <div style={{ backgroundColor: '#F5F5F5' }}>
        <div className="w-full px-8 py-8 overflow-x-hidden">
        {/* Tabs */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex items-center gap-8 border-b" style={{ borderColor: '#E5E7EB' }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setSearchQuery('');
                    setFilterStatus('all');
                    setSchemaCurrentPage(1);
                  }}
                  className={`pb-3 font-medium transition-all relative ${
                    activeTab === tab.id ? 'border-b-2' : ''
                }`}
                style={{
                    color: activeTab === tab.id ? COLORS.admin.primary : '#6B7280',
                    borderColor: activeTab === tab.id ? COLORS.admin.primary : 'transparent',
                }}
              >
                {tab.name} ({tab.count})
              </button>
            ))}
          </div>
        </div>

          {/* Action Bar with Search and Filters */}
          <div className="flex items-center justify-between gap-4 mb-6">
            {/* Search - Left */}
            <div className="relative flex-shrink-0" style={{ width: '300px' }}>
              <svg className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder={
                  activeTab === 'questions' ? 'Search questions...' :
                  activeTab === 'schemas' ? 'Search schemas...' :
                  'Search submissions...'
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB', color: '#1F2937' }}
              />
            </div>

            {/* Filters and Actions - Right */}
            <div className="flex items-center gap-3 flex-shrink-0">
              {activeTab === 'schemas' && (
                <select
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setSchemaCurrentPage(1);
                  }}
                  className="px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: '#E5E7EB', color: '#1F2937' }}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
              )}

              {activeTab === 'submissions' && (
                <>
                  <select
                    value={filterCompanyId || ''}
                    onChange={(e) => {
                      setFilterCompanyId(e.target.value ? parseInt(e.target.value) : null);
                    }}
                    className="px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ borderColor: '#E5E7EB', color: '#1F2937' }}
                  >
                    <option value="">All Companies</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => loadData()}
                    className="px-4 py-2 rounded-lg border transition-all hover:bg-gray-50 whitespace-nowrap"
                    style={{ borderColor: '#E5E7EB', color: '#1F2937' }}
                  >
                    Refresh
                  </button>
                </>
              )}

              {activeTab === 'questions' && (
                <button
                  onClick={() => setShowCreateQuestionDrawer(true)}
                  className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:shadow-md whitespace-nowrap"
                  style={{ backgroundColor: COLORS.admin.primary }}
                >
                  <svg className="w-4 h-4 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create Question
                </button>
              )}

              {activeTab === 'schemas' && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowGenerateSchemaDrawer(true)}
                    className="px-4 py-2 rounded-lg font-medium transition-all hover:bg-gray-50 whitespace-nowrap"
                    style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
                  >
                    Generate with RAG
                  </button>
                  <button
                    onClick={() => setShowCreateSchemaDrawer(true)}
                    className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:shadow-md whitespace-nowrap"
                    style={{ backgroundColor: COLORS.admin.primary }}
                  >
                    <svg className="w-4 h-4 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create Schema
                  </button>
                </div>
              )}
          </div>
        </div>

        {/* Content */}
          <div className="bg-white rounded-lg shadow-sm p-6">
          {activeTab === 'questions' && (
            <div>
                <h2 className="text-lg font-bold mb-2" style={{ color: '#1F2937' }}>
                Question Bank
              </h2>
                <p className="text-sm mb-4" style={{ color: '#6B7280' }}>
                Manage the global question bank. Questions can be reused across multiple form schemas.
              </p>
                <QuestionList searchQuery={searchQuery} />
            </div>
          )}

          {activeTab === 'schemas' && (
            <div>
                <h2 className="text-lg font-bold mb-2" style={{ color: '#1F2937' }}>
                Form Schemas
              </h2>
                <p className="text-sm mb-4" style={{ color: '#6B7280' }}>
                Create and manage form schemas using questions from the Question Bank.
              </p>
                <div className="space-y-3">
                  {paginatedSchemas.length > 0 ? (
                    <>
                      {paginatedSchemas.map((schema) => {
                        const statusBadge = getStatusBadge(schema.status);
                        return (
                  <div
                    key={schema.id}
                            className="border rounded-lg p-4 hover:bg-gray-50 transition-all cursor-pointer"
                            style={{ borderColor: '#E5E7EB' }}
                    onClick={() => handleViewSchema(schema)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                                  <h3 className="font-semibold text-base" style={{ color: '#1F2937' }}>
                            {schema.form_name}
                          </h3>
                          <span
                                    className="px-2 py-1 rounded-full text-xs font-medium"
                            style={{
                                      backgroundColor: statusBadge.bg,
                                      color: statusBadge.text,
                            }}
                          >
                            {schema.status}
                          </span>
                          {schema.is_current_version && (
                                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Current
                            </span>
                          )}
                        </div>
                                <p className="text-sm mb-2" style={{ color: '#6B7280' }}>
                                  {schema.form_description || 'No description'}
                                </p>
                                <div className="flex items-center gap-4 text-xs" style={{ color: '#9CA3AF' }}>
                          <span className="capitalize">{schema.generation_method.replace('_', ' ')}</span>
                          <span>Version {schema.version}</span>
                          <span>{schema.question_ids.length} questions</span>
                          <span>{new Date(schema.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                              <div className="flex items-center gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleViewVersions(schema.id)}
                                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-gray-100"
                                  style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
                          title="View Versions"
                        >
                          Versions
                        </button>
                        <button
                          onClick={() => handleViewSchema(schema)}
                                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-gray-100"
                                  style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleEditSchema(schema)}
                                  className="px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-all hover:shadow-sm"
                                  style={{ backgroundColor: COLORS.admin.primary }}
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  </div>
                        );
                      })}

                      {/* Pagination for Schemas */}
                      {schemaTotalPages > 1 && (
                        <div className="mt-6 pt-4 border-t flex flex-wrap items-center justify-between gap-4" style={{ borderColor: '#E5E7EB' }}>
                          <div className="text-sm" style={{ color: '#6B7280' }}>
                            Showing {(schemaCurrentPage - 1) * itemsPerPage + 1} to {Math.min(schemaCurrentPage * itemsPerPage, filteredSchemas.length)} of {filteredSchemas.length} schemas
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSchemaCurrentPage(prev => Math.max(1, prev - 1))}
                              disabled={schemaCurrentPage === 1}
                              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                              style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
                            >
                              Previous
                            </button>
                            <span className="px-3 py-1.5 text-sm font-medium" style={{ color: '#6B7280' }}>
                              Page {schemaCurrentPage} of {schemaTotalPages}
                            </span>
                            <button
                              onClick={() => setSchemaCurrentPage(prev => Math.min(schemaTotalPages, prev + 1))}
                              disabled={schemaCurrentPage === schemaTotalPages}
                              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                              style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                  <div className="bg-gray-50 rounded-lg p-8 text-center">
                      <svg className="w-12 h-12 mx-auto mb-3" style={{ color: '#D1D5DB' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-sm mb-4" style={{ color: '#9CA3AF' }}>
                        {searchQuery || filterStatus !== 'all' ? 'No schemas found matching your filters' : 'No form schemas yet'}
                      </p>
                      <div className="flex items-center justify-center gap-3">
                      <button
                          onClick={() => setShowGenerateSchemaDrawer(true)}
                          className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:bg-gray-100"
                          style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
                      >
                        Generate with RAG
                      </button>
                      <button
                        onClick={() => setShowCreateSchemaDrawer(true)}
                          className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all hover:shadow-md"
                          style={{ backgroundColor: COLORS.admin.primary }}
                      >
                        Create Schema
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'submissions' && (
            <div>
                <h2 className="text-lg font-bold mb-2" style={{ color: '#1F2937' }}>
                Form Submissions
              </h2>
                <p className="text-sm mb-4" style={{ color: '#6B7280' }}>
                View and manage form submissions. Enrich submissions with document data.
              </p>
                <SubmissionList 
                  onViewSubmission={handleViewSubmission} 
                  searchQuery={searchQuery}
                  filterCompanyId={filterCompanyId}
                  onRefresh={loadData}
                  companies={companies}
                />
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Create Question Drawer */}
      <QuestionDrawer
        mode="create"
        isOpen={showCreateQuestionDrawer}
        onClose={() => setShowCreateQuestionDrawer(false)}
        onSuccess={handleQuestionSuccess}
      />

      {/* Create Schema Drawer */}
      <CreateSchemaDrawer
        isOpen={showCreateSchemaDrawer}
        onClose={() => setShowCreateSchemaDrawer(false)}
        onSuccess={handleSchemaSuccess}
      />

      {/* Generate Schema Drawer */}
      <GenerateSchemaDrawer
        isOpen={showGenerateSchemaDrawer}
        onClose={() => setShowGenerateSchemaDrawer(false)}
        onSuccess={handleSchemaSuccess}
      />

      {/* Schema Versions Drawer */}
      <SchemaVersionsDrawer
        schemaId={selectedSchemaId}
        isOpen={showSchemaVersionsDrawer}
        onClose={() => {
          setShowSchemaVersionsDrawer(false);
          setSelectedSchemaId(null);
        }}
      />

      {/* Schema Drawer */}
      <SchemaDrawer
        mode={schemaDrawerMode}
        schema={selectedSchema}
        isOpen={showSchemaDrawer}
        onClose={() => {
          setShowSchemaDrawer(false);
          setSelectedSchema(null);
        }}
        onSuccess={handleSchemaSuccess}
      />

      {/* Submission Drawer */}
      <SubmissionDrawer
        submission={selectedSubmission}
        submissionId={selectedSubmissionId}
        isOpen={showSubmissionDrawer}
        onClose={() => {
          setShowSubmissionDrawer(false);
          setSelectedSubmission(null);
          setSelectedSubmissionId(null);
        }}
      />
    </AdminLayout>
  );
};